/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
// Extends From
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { setDiff, sort } from '@ember/object/computed';
// External imports
import SubjectReportVisibility from '@linc-technologies/linced-common/enums/subject-report-visibility';
import subjectTypes from '@linc-technologies/linced-common/enums/subject-type';
// Internal imports
import {
  destroyAndSaveRelated,
  loadRelated,
} from 'linced-admin/utils/destroy-record';

export default class IndexController extends Controller {
  // Services
  @service
  paperToaster;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  // Attrs
  @tracked
  subjectDialogOpen = false;

  subjectSaveInProgress = false;

  @tracked
  editingSubject = null;

  @tracked
  subjectLabel = '';

  @action
  setSubjectLabel(value) {
    this.subjectLabel = value;
  }

  @tracked
  subjectDescription = '';

  @action
  setSubjectDescription(value) {
    this.subjectDescription = value;
  }

  @tracked
  subjectTrackable = false;

  @action
  setSubjectTrackable(value) {
    this.subjectTrackable = value;
  }

  @tracked
  subjectBestFits = false;

  @action
  setSubjectBestFits(value) {
    this.subjectBestFits = value;
  }

  @tracked
  subjectVisibleToTeachers = true;

  @action
  setSubjectVisibleToTeachers(value) {
    this.subjectVisibleToTeachers = value;
  }

  @tracked
  subjectVisibleToParents = true;

  @action
  setSubjectVisibleToParents(value) {
    this.subjectVisibleToParents = value;
  }

  @tracked
  subjectVisibleToLearners = true;

  @action
  setSubjectVisibleToLearners(value) {
    this.subjectVisibleToLearners = value;
  }

  @tracked
  subjectReportVisibility = SubjectReportVisibility.IF_PAGE_ON_REPORT;

  @tracked
  subjectType = Object.freeze({ value: 0, label: 'Not set' });

  @tracked
  showRadialDials = true;

  @action
  setShowRadialDials(value) {
    this.showRadialDials = value;
  }

  @tracked
  entityToDelete = null;

  @tracked
  deleteErrorMessage = null;

  @tracked
  deleteDialogOpen = false;

  reportVisibilityOptions = Object.freeze([
    { value: SubjectReportVisibility.ALWAYS, label: 'Yes' },
    { value: SubjectReportVisibility.NEVER, label: 'No' },
    {
      value: SubjectReportVisibility.IF_PAGE_ON_REPORT,
      label: "If on a Page that's visible on Reports",
    },
  ]);

  // Sorting
  sortDialogOpen = false;

  goalsSort = Object.freeze(['sequence', 'label']);

  @sort('filteredGoals', 'goalsSort')
  sortedAndFilteredGoals;

  @sort('model.subjects', 'goalsSort')
  sortedGoals;

  // Usage sets attributes
  @tracked
  currentSubject = null;

  @tracked
  usageSetDialogOpen = false;

  @tracked
  selectedUsageSets = [];

  @tracked
  initialUsageSets = [];

  // Subject Types
  subjectTypes = subjectTypes;

  // Filters
  @setDiff('model.usageSets', 'selectedUsageSetsFilter')
  remainingUsageSetsFilter;

  @tracked
  selectedUsageSetsFilter = [];

  @tracked
  deleteInProgress;

  @tracked
  saveInProgress;

  @tracked
  isTopic;

  @tracked
  subjectIsTopic;

  @action
  setSubjectIsTopic(value) {
    this.subjectIsTopic = value;
  }

  @computed('sortedGoals.[]', 'selectedUsageSetsFilter.[]')
  get filteredGoals() {
    return this.get('sortedGoals').filter((goal) => {
      if (!isEmpty(this.selectedUsageSetsFilter)) {
        return this.selectedUsageSetsFilter.some((usageSet) =>
          goal.get('usageSetsArray').includes(usageSet),
        );
      }
      return true;
    });
  }

  // recordsReferencing returns the usage sets and page templates that point
  // at a goal subject. They get saved after it is deleted so the server drops
  // the stale id; destroyAndSaveRelated explains why they are gathered first.
  async recordsReferencing(subject) {
    const [usageSets, pageTemplates] = await Promise.all([
      loadRelated(subject, ['usageSetIds']),
      this.store.query('posts/page', {
        goalSubjectIds: JSON.stringify([subject.id]),
      }),
    ]);
    return [...usageSets, ...pageTemplates.slice()];
  }

  @action
  handleTypeSelect(selectedOption) {
    if (selectedOption.value === null) {
      // Close the dropdown without changing selection
    } else {
      // Update the selection
      this.subjectType = selectedOption;
    }
  }

  @action
  addSubject() {
    this.resetSubject();
    this.subjectDialogOpen = true;
  }

  @action
  editSubject(subject) {
    this.resetSubject();

    let subjectType = { value: 0, label: 'Not set' };
    if (typeof subject.subjectType === 'number') {
      let foundSubjectType = subjectTypes.values.find(
        (e) => e.value === subject.subjectType,
      );
      if (foundSubjectType) {
        subjectType = foundSubjectType;
      }
    }

    this.editingSubject = subject;
    this.subjectLabel = subject.label;
    this.subjectDescription = subject.description;
    this.subjectTrackable = subject.trackable;
    this.subjectBestFits = subject.calculateBestFits;
    this.subjectVisibleToTeachers = subject.visibleToTeachers;
    this.subjectVisibleToParents = subject.visibleToParents;
    this.subjectVisibleToLearners = subject.visibleToLearners;
    this.subjectReportVisibility = subject.reportVisibility;
    this.showRadialDials = subject.showRadialDials;
    this.subjectIsTopic = subject.isTopic;
    this.subjectType = subjectType;
    this.subjectDialogOpen = true;
  }

  @action
  setSubjectReportVisibility(selectedOption) {
    this.subjectReportVisibility = selectedOption.value;
  }

  @action
  closeSubjectDialog() {
    this.subjectDialogOpen = false;
  }

  @action
  saveSubject() {
    let subject = this.editingSubject;
    let toaster = this.paperToaster;

    if (isEmpty(subject)) {
      subject = this.store.createRecord('goals/subject', {
        schoolId: this.get('session.school'),
      });
    }

    subject.setProperties({
      label: this.subjectLabel,
      description: this.subjectDescription,
      trackable: this.subjectTrackable,
      calculateBestFits: this.subjectBestFits,
      visibleToTeachers: this.subjectVisibleToTeachers,
      visibleToParents: this.subjectVisibleToParents,
      visibleToLearners: this.subjectVisibleToLearners,
      reportVisibility: this.subjectReportVisibility,
      showRadialDials: this.showRadialDials,
      isTopic: this.subjectIsTopic,
      sequence: this.get('model.subjects.length') + 1,
      subjectType: this.subjectType.value || 0,
    });

    subject
      .save()
      .then(() => {
        this.subjectDialogOpen = false;
        toaster.show(`Goals subject "${subject.get('label')}" saved.`, {
          duration: 4000,
          position: 'top right',
        });
      })
      .catch(() => {
        toaster.show(`Error saving goals subject.`, {
          duration: 4000,
          position: 'top right',
        });
      });
  }

  @action
  deleteSubject(subject) {
    this.entityToDelete = subject;
    this.deleteDialogOpen = true;
    this.deleteErrorMessage = '';
  }

  @action
  async confirmDelete(confirmed) {
    if (!confirmed) {
      // Not confirmed the deletion
      this.deleteDialogOpen = false;
      this.entityToDelete = null;
      this.deleteErrorMessage = '';
      return;
    }

    // Deletion confirmed; actually delete the record.
    this.deleteInProgress = true;
    this.deleteErrorMessage = '';
    let subject = this.entityToDelete;
    try {
      let related = await this.recordsReferencing(subject);

      // Delete record (locally + pushed to server), then save the records
      // that referenced it.
      let { deleted, error } = await destroyAndSaveRelated(subject, related);
      if (!deleted) {
        this.deleteErrorMessage = deleteErrorMessage(error);
        return;
      }
      if (error) {
        this.toaster.error(
          `Goal subject deleted, but removing it from its usage sets and page templates failed.`,
        );
      } else {
        this.toaster.success(`Goal subject deleted successfully.`);
      }

      // Tidy up / close dialog.
      this.deleteDialogOpen = false;
      this.entityToDelete = null;
      this.deleteErrorMessage = '';
    } catch (err) {
      // Finding the referencing records failed; nothing has been deleted.
      this.deleteErrorMessage = deleteErrorMessage(err);
    } finally {
      this.deleteInProgress = false;
    }
  }

  @action
  closeDeleteDialog() {
    this.deleteDialogOpen = false;
    this.entityToDelete = null;
  }

  @action
  openUsageSetDialog(subject) {
    subject
      .get('usageSetIds')
      .then((usageSetsArray) => {
        this.usageSetDialogOpen = true;
        this.currentSubject = subject;
        this.selectedUsageSets = usageSetsArray.slice();
        this.initialUsageSets = usageSetsArray.slice();
      })
      .catch(() => {
        this.toaster.error(
          `Error trying to edit the goal subject's usage sets`,
        );
      });
  }

  @action
  closeUsageSetDialog() {
    // The pending save's callbacks still need the state this clears.
    if (this.saveInProgress) {
      return;
    }
    this.usageSetDialogOpen = false;
    this.currentSubject = null;
    this.selectedUsageSets = [];
    this.initialUsageSets = [];
  }

  @action
  addUsageSet(usageSet, checked) {
    if (checked) {
      this.selectedUsageSets = [...this.selectedUsageSets, usageSet];
    } else {
      this.selectedUsageSets = this.selectedUsageSets.filter(
        (u) => u !== usageSet,
      );
    }
  }

  @action
  addUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = [...this.selectedUsageSetsFilter, usageSet];
  }

  @action
  removeUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = this.selectedUsageSetsFilter.filter(
      (u) => u !== usageSet,
    );
  }

  @action
  saveSubjectAndUsageSet() {
    this.saveInProgress = true;
    let promises = [];
    let changedUsageSets = [];

    // Update the usage sets
    let removedUsageSets = this.initialUsageSets.filter(
      (usageSet) => !this.selectedUsageSets.includes(usageSet),
    );
    let addedUsageSets = this.selectedUsageSets.filter(
      (usageSet) => !this.initialUsageSets.includes(usageSet),
    );
    changedUsageSets = changedUsageSets.concat(
      removedUsageSets,
      addedUsageSets,
    );

    if (!isEmpty(changedUsageSets)) {
      // Usage sets a page has been newly added to/removed from
      changedUsageSets.forEach((changedUsageSet) => {
        promises.push(changedUsageSet.save());
      });
    }

    // Save the goal subject at the end
    this.currentSubject.set('usageSetIds', this.selectedUsageSets);
    promises.push(this.currentSubject.save());

    return Promise.all(promises)
      .then(() => {
        // After saving the page and all involved usage sets
        this.toaster.success(
          `Successfully saved the the goal subject and its usage sets`,
        );
        this.currentSubject.reload();
        this.usageSetDialogOpen = false;
        this.currentSubject = null;
        this.selectedUsageSets = [];
        this.initialUsageSets = [];
        this.saveInProgress = false;
      })
      .catch(() => {
        this.toaster.error(`Error saving the goal subject's usage sets`);
        // Put the usage sets back, which also reverts each usage set's side.
        // rollbackAttributes() does not touch relationships.
        this.currentSubject.set('usageSetIds', this.initialUsageSets);
        this.currentSubject.rollbackAttributes();
        this.saveInProgress = false;
      });
  }

  // unpublishGoalSubject will remove the goal subject from all usage sets so it becomes unusable
  @action
  unpublishGoalSubject(subject) {
    subject.get('usageSetIds').then((usageSetsArray) => {
      this.selectedUsageSets = [];
      this.initialUsageSets = usageSetsArray.slice();
      this.currentSubject = subject;
      this.saveSubjectAndUsageSet();
    });
  }

  resetSubject() {
    this.subjectLabel = '';
    this.subjectDescription = '';
    this.subjectTrackable = false;
    this.subjectBestFits = false;
    this.subjectVisibleToTeachers = true;
    this.subjectVisibleToParents = true;
    this.subjectVisibleToLearners = true;
    this.subjectReportVisibility = SubjectReportVisibility.IF_PAGE_ON_REPORT;
    this.showRadialDials = false;
    this.isTopic = false;
    this.subjectType = 0;
  }
}

// deleteErrorMessage picks the message to show for a failed delete.
function deleteErrorMessage(err) {
  if (!isEmpty(err)) {
    if (err.errors && err.errors.length > 0) {
      return err.errors[0].detail;
    }
    if (err.message) {
      return err.message;
    }
  }
  return 'An unknown error occurred. Please try again.';
}
